import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { InfoIcon } from './icons';

/** Enough for a sentence or two, and never wider than the viewport allows. */
const PANEL_WIDTH = 320;
/** Breathing room from the window edges, and between the icon and the panel. */
const MARGIN = 16;
const GAP = 8;

/**
 * The (i) beside a label, and what it holds.
 *
 * For the sentence that is true forever and read once: what a health check IS,
 * what a root directory means. That text was sitting open under every field,
 * on every visit, for the one visit in fifty that needed it — and it is the
 * reason a form of six questions read as a page of prose.
 *
 * It is NOT for anything that changes what you would type. A default, a
 * consequence, a rule that costs a deploy when it is missed: those stay on the
 * screen. A person who did not know to press this never reads what is behind
 * it, so nothing that matters can live here.
 *
 * OPENS ON PRESS, not on hover. There was a hover tooltip here once, wired
 * through `data-tip`, and it was unreachable on the device where the text is
 * most in the way — a phone has no hover, so the (i) was decoration. Press
 * opens, press again closes, Escape closes, a press anywhere else closes, and
 * the button is a real button so the keyboard reaches it in order.
 *
 * The panel is PORTALLED to the body and positioned in viewport coordinates.
 * Absolutely positioned inside the label, it was clipped the moment a field
 * sat in a card with `overflow-hidden` — which is every card on the create
 * form — so the sentence it exists to show arrived with its last line cut off.
 * Nothing between here and the body can crop it now.
 */
const InfoTip = ({ children, label }: {
  children: React.ReactNode,
  /** Names the subject for a screen reader: "About the health check". */
  label: string,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number, left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // Under the icon, and pulled back from the right edge when the field it
  // belongs to sits at the end of a two-column row.
  const place = useCallback(() => {
    const button = buttonRef.current;

    if (!button) {
      return;
    }

    const rect = button.getBoundingClientRect();
    const rightmost = window.innerWidth - MARGIN - PANEL_WIDTH;

    setPosition({
      top: rect.bottom + GAP,
      left: Math.max(MARGIN, Math.min(rect.left, rightmost)),
    });
  }, []);

  // Before paint, so it never appears at the wrong place first.
  useLayoutEffect(() => {
    if (isOpen) {
      place();
    }
  }, [isOpen, place]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Pointer rather than click: a press that starts outside should close it
    // even if the release lands somewhere else, and `mousedown` alone misses
    // touch on older WebKit. The panel is outside this component's DOM now, so
    // it has to be asked separately whether the press was its own.
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;

      if (
        !buttonRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    // `true` for capture: the form scrolls inside its own container, not the
    // window, and a scroll event on a nested element does not bubble.
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [isOpen, place]);

  return (
    <span className="relative inline-flex items-center align-middle">
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        // A 24px target around a 14px glyph, pulled back by its own padding so
        // the icon still sits where it is drawn next to the label.
        className={`b4-focus -m-[5px] p-[5px] rounded-full transition-colors duration-[120ms] ${
          isOpen ? 'text-white' : 'text-navy-300 hover:text-white'
        }`}
        onClick={() => setIsOpen((wasOpen) => !wasOpen)}
      >
        <InfoIcon size={14} />
      </button>
      {isOpen && position
        ? createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="tooltip"
            style={{
              top: position.top,
              left: position.left,
              width: `min(${PANEL_WIDTH}px, calc(100vw - ${MARGIN * 2}px))`,
            }}
            className="fixed z-[100] rounded-card border border-navy-400 bg-navy-700 px-3 py-2.5 text-ds-115 leading-[1.5] font-normal text-navy-100 shadow-[0_8px_24px_rgba(0,0,0,0.4)]"
          >
            {children}
          </div>,
          document.body
        )
        : null}
    </span>
  );
};

export default InfoTip;
